import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Mail,
  Globe,
  TrendingUp,
  TrendingDown,
  Heart,
  Calendar,
  ShoppingBag,
  Link,
  Briefcase,
  User,
  Sparkles,
  ExternalLink,
  ClipboardCheck,
  MapPin,
  Package,
  type LucideIcon,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/marketing/components/ui/sheet";
import { Badge } from "@/marketing/components/ui/badge";
import { Separator } from "@/marketing/components/ui/separator";
import type { Influencer } from "@/marketing/models/influencer";
import { REGION_OPTIONS } from "@/marketing/models/influencer";
import { formatNumber } from "@/marketing/lib/formatters";
import { getSocialMediaUrl, getPlatformLabel } from "@/marketing/lib/socialMediaUrls";
import { INFLUENCER_QUERIES } from "@/marketing/infrastructure/influencer/utils/queries";

interface InfluencerProfileModalProps {
  influencer: Influencer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
  color?: string | undefined;
}) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-1 tw-rounded-lg tw-border tw-p-3">
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-muted-foreground">
        <Icon className="tw-h-3.5 tw-w-3.5" />
        {label}
      </div>
      <p
        className="tw-text-sm tw-font-semibold"
        style={color ? { color } : undefined}
      >
        {value}
      </p>
    </div>
  );
}

function BooleanFlag({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: boolean;
  icon: LucideIcon;
}) {
  return (
    <div
      className={`tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium ${
        value ? "tw-bg-green-50 tw-text-green-700" : "tw-bg-neutral-100 tw-text-neutral-400"
      }`}
    >
      <Icon className="tw-h-3 tw-w-3" />
      {label}
    </div>
  );
}

function InfoField({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <div className="tw-flex tw-items-start tw-gap-2 tw-text-sm">
      <Icon className="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-muted-foreground" />
      <div>
        <span className="tw-text-xs tw-text-muted-foreground">{label}</span>
        {children}
      </div>
    </div>
  );
}

export function InfluencerProfileModal({
  influencer,
  open,
  onOpenChange,
}: InfluencerProfileModalProps) {
  const { data: detail, isLoading } = useQuery({
    ...INFLUENCER_QUERIES.detail(influencer),
    enabled: open && influencer !== null,
  });

  // Preserve applicationData from the original influencer (DynamoDB) since detail (MeiliSearch) doesn't have it
  const profile = useMemo(() => {
    if (!influencer) return null;
    return detail
      ? {
          ...detail,
          applicationData: detail.applicationData ?? influencer.applicationData,
        }
      : influencer;
  }, [detail, influencer]);

  if (!influencer || !profile) return null;

  const region = REGION_OPTIONS.find(
    (r) => r.value === profile.countrySignals?.[0],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="tw-overflow-y-auto sm:tw-max-w-lg">
        <SheetHeader className="tw-p-6 tw-pb-0">
          <div className="tw-flex tw-items-start tw-gap-4">
            {influencer.profilePictureUrl ? (
              <img
                src={influencer.profilePictureUrl}
                alt={influencer.displayName}
                className="tw-h-16 tw-w-16 tw-shrink-0 tw-rounded-full tw-object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <div className="tw-flex tw-h-16 tw-w-16 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted tw-text-lg tw-font-medium">
                {(influencer.displayName ?? "")
                  .split(" ")
                  .filter(Boolean)
                  .map((w) => w[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase() || "?"}
              </div>
            )}
            <div className="tw-min-w-0 tw-flex-1">
              <SheetTitle className="tw-flex tw-items-center tw-gap-2">
                {influencer.displayName}
                {influencer.isVerified && (
                  <Badge variant="secondary" className="tw-text-[10px]">
                    Verified
                  </Badge>
                )}
              </SheetTitle>
              <SheetDescription className="tw-mt-1">
                <a
                  href={getSocialMediaUrl(
                    influencer.platform,
                    influencer.handle,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[#2563EB] hover:tw-underline"
                >
                  @{influencer.handle}
                  <ExternalLink className="tw-h-3 tw-w-3" />
                </a>
                <Badge
                  variant="outline"
                  className="tw-ml-2 tw-text-[10px] tw-font-normal"
                >
                  {getPlatformLabel(influencer.platform)}
                </Badge>
              </SheetDescription>
              <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-muted-foreground">
                {profile.emails?.length > 0 && (
                  <span className="tw-flex tw-items-center tw-gap-1">
                    <Mail className="tw-h-3 tw-w-3" />
                    {profile.emails[0]}
                  </span>
                )}
                {profile.languageSignals?.length > 0 && (
                  <span className="tw-flex tw-items-center tw-gap-1">
                    <Globe className="tw-h-3 tw-w-3" />
                    {profile.languageSignals[0]?.toUpperCase()}
                  </span>
                )}
                {region && (
                  <Badge variant="outline" className="tw-text-[10px] tw-font-normal">
                    {region.flag} {region.label}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </SheetHeader>

        <div className="tw-p-6">
          {isLoading ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
              <p className="tw-text-sm tw-text-muted-foreground">
                Loading enrichment data...
              </p>
            </div>
          ) : (
            <div className="tw-space-y-5">
              {/* Stats Grid */}
              <div>
                <h3 className="tw-mb-3 tw-text-sm tw-font-medium tw-text-muted-foreground">
                  Metrics
                </h3>
                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                  <StatCard
                    label="Followers"
                    value={formatNumber(profile.followersCount ?? 0)}
                    icon={User}
                  />
                  <StatCard
                    label="Engagement"
                    value={`${(profile.engagementRate ?? 0).toFixed(2)}%`}
                    icon={Heart}
                    color={
                      (profile.engagementRate ?? 0) >= 5 ? "#16a34a" : undefined
                    }
                  />
                  <StatCard
                    label="Avg. Likes"
                    value={
                      profile.avgLikes != null
                        ? formatNumber(profile.avgLikes)
                        : "-"
                    }
                    icon={Heart}
                  />
                  <StatCard
                    label="Follower Growth"
                    value={
                      profile.followerGrowthRate != null &&
                      !isNaN(profile.followerGrowthRate)
                        ? `${profile.followerGrowthRate >= 0 ? "+" : ""}${profile.followerGrowthRate.toFixed(1)}%`
                        : "-"
                    }
                    icon={
                      (profile.followerGrowthRate ?? 0) >= 0
                        ? TrendingUp
                        : TrendingDown
                    }
                    color={
                      profile.followerGrowthRate == null
                        ? undefined
                        : profile.followerGrowthRate >= 0
                          ? "#16a34a"
                          : "#dc2626"
                    }
                  />
                  <StatCard
                    label="Posts/Month"
                    value={
                      profile.postingFrequency != null &&
                      !isNaN(profile.postingFrequency)
                        ? profile.postingFrequency.toFixed(1)
                        : "-"
                    }
                    icon={Calendar}
                  />
                  <StatCard
                    label="Avg. Views"
                    value={
                      profile.avgViews != null
                        ? formatNumber(profile.avgViews)
                        : "-"
                    }
                    icon={User}
                  />
                </div>
              </div>

              <Separator />

              {/* Topics */}
              {profile.topics && profile.topics.length > 0 && (
                <>
                  <div>
                    <h3 className="tw-mb-3 tw-text-sm tw-font-medium tw-text-muted-foreground">
                      Topics
                    </h3>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                      {profile.topics.map((topic) => (
                        <Badge
                          key={topic}
                          className="tw-bg-blue-50 tw-text-blue-700 hover:tw-bg-blue-100"
                        >
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Bio */}
              {profile.bioText && (
                <>
                  <div>
                    <h3 className="tw-mb-2 tw-text-sm tw-font-medium tw-text-muted-foreground">
                      Bio
                    </h3>
                    <p className="tw-text-sm tw-leading-relaxed">{profile.bioText}</p>
                  </div>
                  <Separator />
                </>
              )}

              {/* Hashtags */}
              {profile.hashtags && profile.hashtags.length > 0 && (
                <>
                  <div>
                    <h3 className="tw-mb-2 tw-text-sm tw-font-medium tw-text-muted-foreground">
                      Hashtags
                    </h3>
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                      {[...new Set(profile.hashtags)].map((tag) => (
                        <Badge
                          key={tag}
                          variant="secondary"
                          className="tw-text-xs tw-font-normal"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Ambassador Application */}
              {profile.applicationData && (
                <>
                  <div>
                    <h3 className="tw-mb-3 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-muted-foreground">
                      <ClipboardCheck className="tw-h-3.5 tw-w-3.5" />
                      Ambassador Application
                    </h3>
                    <div className="tw-space-y-2.5">
                      <InfoField label="Brands" icon={Package}>
                        <p className="tw-font-medium">
                          {profile.applicationData.openToAnyBrand
                            ? "Open to any brand"
                            : profile.applicationData.brands?.join(", ") || "-"}
                        </p>
                      </InfoField>
                      <InfoField label="Location" icon={MapPin}>
                        <p className="tw-font-medium">
                          {[
                            profile.applicationData.city,
                            profile.applicationData.state,
                            profile.applicationData.country,
                          ]
                            .filter(Boolean)
                            .join(", ") || "-"}
                        </p>
                      </InfoField>
                      <InfoField label="Contact" icon={Mail}>
                        <p className="tw-font-medium">
                          {profile.applicationData.email || "-"}
                        </p>
                        <p className="tw-text-xs tw-text-muted-foreground">
                          {profile.applicationData.phoneCode}{" "}
                          {profile.applicationData.phone}
                        </p>
                      </InfoField>
                      <InfoField label="Shipping Address" icon={MapPin}>
                        <p className="tw-font-medium">
                          {profile.applicationData.address1}
                          {profile.applicationData.address2
                            ? `, ${profile.applicationData.address2}`
                            : ""}
                        </p>
                        <p className="tw-text-xs tw-text-muted-foreground">
                          {profile.applicationData.city},{" "}
                          {profile.applicationData.state}{" "}
                          {profile.applicationData.zipCode}
                        </p>
                      </InfoField>
                      <InfoField label="Availability" icon={Calendar}>
                        <p className="tw-font-medium">
                          {profile.applicationData.availability}
                          {profile.applicationData.availabilityWhen
                            ? ` — ${profile.applicationData.availabilityWhen}`
                            : ""}
                        </p>
                      </InfoField>
                      <InfoField label="Content Commitment" icon={Sparkles}>
                        <p className="tw-font-medium">
                          {profile.applicationData.contentCount}
                        </p>
                      </InfoField>
                      {profile.applicationData.appliedAt && (
                        <p className="tw-text-xs tw-text-muted-foreground">
                          Applied{" "}
                          {new Date(
                            profile.applicationData.appliedAt,
                          ).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Boolean Flags */}
              <div>
                <h3 className="tw-mb-3 tw-text-sm tw-font-medium tw-text-muted-foreground">
                  Profile Attributes
                </h3>
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  <BooleanFlag
                    label="Brand Deals"
                    value={profile.hasBrandDeals ?? false}
                    icon={Briefcase}
                  />
                  <BooleanFlag
                    label="Business"
                    value={profile.isBusiness ?? false}
                    icon={ShoppingBag}
                  />
                  <BooleanFlag
                    label="Creator"
                    value={profile.isCreator ?? false}
                    icon={Sparkles}
                  />
                  <BooleanFlag
                    label="Merch"
                    value={profile.hasMerch ?? false}
                    icon={ShoppingBag}
                  />
                  <BooleanFlag
                    label="Link in Bio"
                    value={profile.hasLinkInBio ?? false}
                    icon={Link}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
